<template>
  <div>
    <h1>{{ $t('Close Register') }}</h1>
    <v-row
      class="mt-4"
      wrap
    >
      <v-col sm="3">
        <v-card height="100%">
          <v-card-title>
            <h2 class="text-h6">{{ $t('Cash Register') }}</h2>
          </v-card-title>
          <v-divider />
          <v-list>
            <v-list-item>
              <v-list-item-content>{{ $t('Store') }}</v-list-item-content>
              <v-list-item-content class="align-end">{{ _.get(cashRegister, 'store.name') }}</v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>{{ $t('Cash Register') }}</v-list-item-content>
              <v-list-item-content class="align-end">{{ _.get(cashRegister, 'description') }}</v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>{{ $t('Closure #') }}</v-list-item-content>
              <v-list-item-content class="align-end">{{ _.get(cashRegister, 'openedReport.nr') }}</v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>{{ $t('Opened At') }}</v-list-item-content>
              <v-list-item-content class="align-end">{{ _.get(cashRegister, 'openedReport.created') | moment("D MMMM YYYY HH:mm") }}</v-list-item-content>
            </v-list-item>

            <v-divider />
            <v-subheader>{{ $t('Cash Flow') }}</v-subheader>
            <v-list-item @click="showCashMovementsDialog = true">
              <v-list-item-content>{{ `${$t('Cash-In')} + ${$t('Cash-Out')}` }}</v-list-item-content>
              <v-list-item-content class="align-end"> {{ cashMovementsTotal | money }} </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-for="method in paymentMethods"
              :key="method.id"
            >
              <v-list-item-content>{{ $t('{0} Payments', [method.name]) }}</v-list-item-content>
              <v-list-item-content class="align-end">{{ getAmountByPaymentMethod(salesTotals, method.id) | money }}</v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
      <v-col sm="9">
        <v-card
          class="mt-0 card-flex"
          height="100%"
        >
          <div class="pt-2 pl-6 pr-6 flex-grow-1">
            <v-alert
              class="mt-2 mb-6"
              color="primary"
              icon="fal fa-info-square"
              prominent
              text
              outlined
            >
              {{ $t('Once you complete your register closure, all of the day\'s cash (including any cash movements and float) should be counted. If you decide to remove some redundant cash from the drawer you should enter this at "Safedrop".') }}
            </v-alert>
            <form-wrapper :validator="$v">
              <v-simple-table
                class="table"
                dense
              >
                <thead>
                  <tr>
                    <th class="text-h6" />
                    <th class="text-h6">{{ $t('{0} Amount', [this.$t('Counted')]) }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-if="!countablePaymentMethods.length"
                    style="border-bottom: none"
                  >
                    <td colspan="2">
                      <v-progress-linear
                        :indeterminate="true"
                        class="mt-3 mb-10"
                        height="3"
                      />
                    </td>
                  </tr>
                  <tr
                    v-for="(item, index) in countablePaymentMethods"
                    :key="item.id"
                  >
                    <td class="text-subtitle-1 font-weight-bold valign-top">{{ item.name }}</td>
                    <td class="text-subtitle-1 valign-top">
                      <v-menu
                        v-model="showKeyboard[item.id]"
                        :close-on-content-click="false"
                        :nudge-bottom="-20"
                        :nudge-left="5"
                        bottom
                        eager
                        max-width="300px"
                        offset-y
                      >
                        <template v-slot:activator="{ on }">
                          <form-group :name="`countedTotals.$each.${index}.amount`">
                            <template v-slot:default="{attrs}">
                              <v-text-field
                                v-bind="attrs"
                                :disabled="item === cashPaymentMethod && hasDetailedCashCount"
                                :value="getAmountByPaymentMethod(countedTotals, item.id) || 0 | money"
                                autocomplete="off"
                                background-color="#f5f5f5"
                                class="mb-0 elevation-0 black--text"
                                readonly
                                solo
                                style="max-width: 250px"
                                v-on="on"
                                @focus="setPaymentsAmountFocus(index)"
                              >
                                <template
                                  v-slot:append
                                  v-if="item === cashPaymentMethod"
                                >
                                  <v-dialog
                                    v-model="showDetailedCountDialog"
                                    eager
                                    max-width="550px"
                                  >
                                    <forms-cash-registers-detailed-count
                                      ref="detailedCountForm"
                                      currency="EUR"
                                      @mounted="processDetailedCashCount"
                                      @saved="processDetailedCashCount"
                                    />
                                  </v-dialog>
                                  <v-tooltip right>
                                    <template v-slot:activator="{ on }">
                                      <v-btn
                                        icon
                                        v-on="on"
                                        @click="showDetailedCountDialog = true"
                                      >
                                        <v-icon>fal fa-calculator</v-icon>
                                      </v-btn>
                                    </template>
                                    <span>{{ $t('Enter a {0}', [ $t('Detailed cash count').toLowerCase()]) }}</span>
                                  </v-tooltip>
                                </template>
                              </v-text-field>
                            </template>
                          </form-group>
                        </template>
                        <v-card color="grey lighten-5">
                          <v-card-text>
                            <numeric-keyboard
                              :clear-input="showKeyboard[item.id]"
                              :keyboard-class="`keyboard-${item.id}`"
                              :ref="`keyboard-${item.id}`"
                              @clear="setCountedTotal(null, item.id)"
                              @input="setCountedTotal($event, item.id)"
                            >
                              <v-text-field
                                v-comma
                                slot="input"
                                slot-scope="{ setKeyboardInput }"
                                :ref="`inputPaymentsAmount`"
                                :label="$t('Amount')"
                                :value="getAmountByPaymentMethod(countedTotals, item.id)"
                                hide-details
                                filled
                                @input="setCountedTotal($event, item.id);setKeyboardInput($event)"
                                @keypress.enter="showKeyboard[item.id] = false"
                              />
                            </numeric-keyboard>
                          </v-card-text>
                        </v-card>
                      </v-menu>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td class="text-subtitle-1 font-weight-bold valign-top">{{ $t('Safedrop') }}</td>
                    <td
                      colspan="3"
                      class="valign-top"
                    >
                      <v-menu
                        v-model="showKeyboard['safedrop']"
                        :close-on-content-click="false"
                        :nudge-bottom="-20"
                        :nudge-left="5"
                        bottom
                        eager
                        max-width="300px"
                        offset-y
                      >
                        <template v-slot:activator="{ on }">
                          <form-group name="safedropAmount">
                            <template slot-scope="{ attrs }">
                              <v-text-field
                                v-bind="attrs"
                                :value="safedropAmount | money"
                                background-color="#f5f5f5"
                                class="mb-0 elevation-0"
                                readonly
                                solo
                                style="max-width: 250px"
                                v-on="on"
                                @focus="setSafedropAmountFocus()"
                              />
                            </template>
                          </form-group>
                        </template>
                        <v-card class="grey lighten-5">
                          <v-card-text>
                            <numeric-keyboard
                              :clear-input="showKeyboard['safedrop']"
                              keyboard-class="keyboard-safedrop"
                              @clear="safedropAmount = null"
                              @input="safedropAmount = parseFloat($event)"
                            >
                              <v-text-field
                                v-comma
                                slot="input"
                                slot-scope="{ setKeyboardInput }"
                                ref="inputSafedropAmount"
                                v-model.number="safedropAmount"
                                :label="$t('Amount')"
                                filled
                                hide-details
                                @input="setKeyboardInput($event)"
                                @keypress.enter="showKeyboard['safedrop'] = false"
                              />
                            </numeric-keyboard>
                          </v-card-text>
                        </v-card>
                      </v-menu>
                    </td>
                  </tr>
                  <tr>
                    <td colspan="2"><v-divider /></td>
                  </tr>
                  <tr>
                    <td class="text-subtitle-1 font-weight-bold">{{ $t('Note') }}</td>
                    <td colspan="3">
                      <v-textarea
                        v-if="cashRegisterReport"
                        v-model="cashRegisterReport.closedNote"
                        :hint="$t('Max. Characters: {0}', ['255'])"
                        auto-grow
                        class="mt-4"
                        counter="255"
                        filled
                        solo
                      />
                    </td>
                  </tr>
                </tfoot>
              </v-simple-table>
            </form-wrapper>
          </div>
          <v-card-actions class="pa-6 align-end">
            <v-btn
              :loading="!expectedTotals.length || $wait.is('closing register')"
              block
              color="primary"
              dark
              large
              @click="closeRegister"
            >{{ $t('Close Register') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <vue-slideout-panel
      v-model="showCashMovementsDialog"
      :widths="['800px']"
      close-html="<i class='fal fa-lg fa-times'></i>"
      @close="showCashMovementsDialog = false"
    >
      <v-card class="elevation-0">
        <v-card-text>
          <cash-movements-table @saved="fetchData" />
        </v-card-text>
      </v-card>
    </vue-slideout-panel>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import cashRegisterService from '@/services/cash-registers'
import CashMovementsTable from '@/components/cash-movements/CashMovementsTable'
import FormsCashRegistersDetailedCount from '@/components/forms/cash-registers/FormsCashRegistersDetailedCount'
import VueSlideoutPanel from 'vue-slideout-panel'
import NumericKeyboard from '@/components/NumericKeyboard'
import validationMixin from '@/utils/mixins/validationMixin'
import { required, decimal, minValue } from 'vuelidate/lib/validators'

export default {
  name: 'FormsCashRegistersClose',

  components: {
    CashMovementsTable,
    FormsCashRegistersDetailedCount,
    NumericKeyboard,
    VueSlideoutPanel
  },

  mixins: [ validationMixin ],

  data () {
    return {
      cashMovementsTotal: 0,
      cashPaymentMethod: null,
      cashRegisterReport: {},
      countablePaymentMethods: [],
      countedTotals: [],
      detailedCountedTotals: [],
      differences: [],
      expectedTotals: [],
      hasDetailedCashCount: false,
      paymentMethods: [],
      safedropAmount: 0,
      salesTotals: null,
      showCashMovementsDialog: false,
      showDetailedCountDialog: false,
      showKeyboard: {}
    }
  },

  validations () {
    const validations = {
      countedTotals: {
        required,
        $each: {
          amount: {
            required,
            decimal,
            minValue: minValue(0)
          }
        }
      },
      safedropAmount: {
        decimal,
        minValue: minValue(0)
      }
    }

    return validations
  },

  computed: {
    ...mapGetters(['cashRegister'])
  },

  watch: {
    'countedTotals': {
      handler: function (counted) {
        // Calculate differences between counted and expected values
        for (let key in this.countedTotals) {
          this.differences[key] = {
            'id': this.countedTotals[key].id,
            'amount': this.countedTotals[key].amount - this.expectedTotals[key].amount
          }
        }
      },
      deep: true
    }
  },

  mounted () {
    this.cashRegisterReport = this.cashRegister.openedReport
    this.fetchData()
  },

  methods: {
    processDetailedCashCount (data) {
      // Check if a detailed count has been entered
      this.hasDetailedCashCount = !_.isEmpty(data.countedValues) && !(_.values(data.countedValues).every(_.isEmpty))

      // Find cashpayment data in countedtotals object
      const countedTotalsIndex = this.countedTotals.findIndex(element => element.id === this.cashPaymentMethod.id)
      const countedTotalObject = { id: this.cashPaymentMethod.id, amount: data.countedTotal }
      // If found, update its data, else push to array
      countedTotalsIndex > -1 ? this.countedTotals.splice(countedTotalsIndex, 1, countedTotalObject) : this.countedTotals.push(countedTotalObject)

      const detailedCountedTotalsIndex = this.detailedCountedTotals.findIndex(element => element.id === this.cashPaymentMethod.id)
      const detailedCountedTotalObject = { id: this.cashPaymentMethod.id, data: data.countedValues }
      // if found, update its data, else push to array
      detailedCountedTotalsIndex > -1 ? this.detailedCountedTotals.splice(detailedCountedTotalsIndex, 1, detailedCountedTotalObject) : this.detailedCountedTotals.push(detailedCountedTotalObject)
      this.showDetailedCountDialog = false
    },
    getAmountByPaymentMethod (data, paymentMethodId) {
      const foundIndex = data.findIndex(element => element.id === paymentMethodId)
      return foundIndex > -1 ? data[foundIndex].amount : 0
    },
    setCountedTotal (amount, paymentMethodId) {
      // Don't try to parse if not a number
      const object = { amount: !isNaN(amount) && amount ? parseFloat(amount) : amount, id: paymentMethodId }
      // Replace edited item in countedTotals array
      const foundIndex = this.countedTotals.findIndex(element => element.id === paymentMethodId)
      foundIndex > -1 ? this.countedTotals.splice(foundIndex, 1, object) : this.countedTotals.push(object)
    },
    setPaymentsAmountFocus (key) {
      setTimeout(() => { this.$nextTick(this.$refs[`inputPaymentsAmount`][key].focus) }, 300)
    },
    setSafedropAmountFocus () {
      setTimeout(() => { this.$nextTick(this.$refs[`inputSafedropAmount`].focus) }, 300)
    },
    closeRegister () {
      if (!this.validate()) return
      const expectedCashAmount = this.getAmountByPaymentMethod(this.expectedTotals, this.cashPaymentMethod.id)
      const countedCashAmount = this.getAmountByPaymentMethod(this.countedTotals, this.cashPaymentMethod.id)
      const cashDifferenceAmount = Math.round((countedCashAmount - expectedCashAmount) * 100) / 100

      if (expectedCashAmount !== countedCashAmount) {
        this.$swal({
          title: this.$t('Warning'),
          text: `${this.$t('The counted amount doesn\'t match the expected amount.')} ${this.$t('The difference is {0}.', [this.$options.filters.money(cashDifferenceAmount)])}`,
          type: 'warning',
          cancelButtonText: this.$t('Cancel'),
          confirmButtonText: this.$t('Continue'),
          showCancelButton: true,
          reverseButtons: true
        })
          .then((result) => {
            if (result.value) {
              this.dispatchClosingToStore()
            }
          })
      } else {
        this.dispatchClosingToStore()
      }
    },
    dispatchClosingToStore () {
      this.$wait.start('closing register')
      return this.$store.dispatch('CLOSE_CASH_REGISTER', {
        cashRegisterReport: this.cashRegisterReport,
        expectedTotals: this.expectedTotals,
        countedTotals: this.countedTotals,
        detailedCountedTotals: this.detailedCountedTotals,
        safedropAmount: this.safedropAmount
      }).then(data => this.helpers.toastSuccess(data.message))
        .catch(data => { if (data && data.message) this.helpers.toastError(data.message) })
        .finally(() => this.$wait.end('closing register'))
    },
    fetchData () {
      cashRegisterService.calculateReportTotals(this.cashRegister.openedReport.uuid)
        .then((data) => {
          this.salesTotals = data.sales
          this.expectedTotals = data.expected
          // Filter the payment methods that should be shown in datatable
          // Search the cash paymentmethod
          this.cashPaymentMethod = data.payment_methods.find((x) => x.code === 'cash')

          // All paymentmethods that should be manually counted
          this.countablePaymentMethods = data.payment_methods.filter((x) => {
            // Only keep cash method in array
            return x.id === this.cashPaymentMethod.id
          })

          // All paymentmethods that have transactions
          this.paymentMethods = data.payment_methods.filter((x) => {
            return this.expectedTotals.findIndex(element => element.id === x.id && element.amount > 0) > -1
          })

          // Calculate the total amount of all cash movements
          this.cashMovementsTotal = data.cash_movements.reduce(function (accumulator, movement) {
            return accumulator + movement.amount
          }, 0)

          // Set all keyboards to not-shown
          // Init the counted totals array, else validation doesn't work
          for (let method of this.countablePaymentMethods) {
            this.$set(this.showKeyboard, method.id, false)
            this.countedTotals.push({
              id: method.id,
              amount: null
            })
          }
        })
    }
  }
}
</script>

<style lang="scss" scoped>
  .v-menu {
    display: block !important
  }
  .table {
    tr:hover {
      background: transparent !important;
    }

    tbody {
      tr:first-child {
        td {
          padding-top: 1em;
        }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/forms/cash-registers/FormsCashRegistersClose.vue